import { logger } from "../../utils/logger";
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { 
  Radio, 
  Save, 
  Copy, 
  Check, 
  Eye, 
  Link, 
  Users, 
  Monitor,
  PlayCircle,
  StopCircle,
  Info,
  ExternalLink
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LiveStreamManager() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const [formData, setFormData] = useState({
    stream_title: "SMSC Live",
    stream_description: "Watch San Marcos Social Club live poker action!",
    is_live: false,
    vdo_view_url: "",
    vdo_embed_url: "",
    guest_invite_url: "",
    room_name: "",
    offline_thumbnail: "",
  });
  const { getAuthHeaders } = useAuth();

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await axios.get(`${API}/stream-config`, getAuthHeaders());
        setFormData({
          stream_title: response.data.stream_title || "SMSC Live",
          stream_description: response.data.stream_description || "",
          is_live: response.data.is_live || false,
          vdo_view_url: response.data.vdo_view_url || "",
          vdo_embed_url: response.data.vdo_embed_url || "",
          guest_invite_url: response.data.guest_invite_url || "",
          room_name: response.data.room_name || "",
          offline_thumbnail: response.data.offline_thumbnail || "",
        });
      } catch (err) {
        logger.error("Error fetching stream config:", err);
        toast.error("Failed to load stream configuration");
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await axios.put(`${API}/stream-config`, formData, getAuthHeaders());
      toast.success("Stream configuration saved!");
    } catch (err) {
      toast.error("Failed to save configuration");
    } finally {
      setSaving(false);
    }
  };

  const toggleLive = async () => {
    const newStatus = !formData.is_live;
    setFormData({ ...formData, is_live: newStatus });
    
    try {
      await axios.put(`${API}/stream-config`, { is_live: newStatus }, getAuthHeaders());
      toast.success(newStatus ? "Stream is now LIVE!" : "Stream is now OFFLINE");
    } catch (err) {
      toast.error("Failed to update live status");
      setFormData({ ...formData, is_live: !newStatus });
    }
  };

  const copyToClipboard = (text, fieldName) => {
    navigator.clipboard.writeText(text);
    setCopiedField(fieldName);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopiedField(null), 2000);
  };

  const publicLiveUrl = `${window.location.origin}/live`;
  
  const embedCode = formData.vdo_embed_url 
    ? `<iframe src="${formData.vdo_embed_url}" width="100%" height="100%" style="aspect-ratio: 16/9;" allow="autoplay; camera; microphone; fullscreen" allowfullscreen></iframe>`
    : "";

  if (loading) {
    return <div className="text-gold animate-pulse">Loading stream configuration...</div>;
  }

  return (
    <div data-testid="live-stream-manager">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl text-white mb-2">Live Stream Manager</h1>
          <p className="text-white/60">Configure your VDO.Ninja live stream</p>
        </div>
        
        {/* Live Status Toggle */}
        <div className="flex items-center gap-4">
          <span className="text-white/60 text-sm">Stream Status:</span>
          <button
            onClick={toggleLive}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-bold uppercase tracking-wider text-sm transition-all ${
              formData.is_live 
                ? 'bg-red-600 hover:bg-red-700 text-white' 
                : 'bg-white/10 hover:bg-white/20 text-white/60'
            }`}
            data-testid="toggle-live-btn"
          >
            {formData.is_live ? (
              <>
                <StopCircle className="w-4 h-4" />
                Go Offline
              </>
            ) : (
              <>
                <PlayCircle className="w-4 h-4" />
                Go Live
              </>
            )}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left Column - Configuration */}
        <div className="space-y-6">
          {/* Stream Info */}
          <form onSubmit={handleSubmit} className="glass-card p-6 space-y-4" data-testid="stream-config-form">
            <h2 className="font-heading text-xl text-white flex items-center gap-2">
              <Radio className="w-5 h-5 text-gold" />
              Stream Information
            </h2>
            
            <div>
              <Label className="text-white/80">Stream Title</Label>
              <Input
                value={formData.stream_title}
                onChange={(e) => setFormData({ ...formData, stream_title: e.target.value })}
                className="bg-casino-secondary border-white/10 text-white mt-1"
                placeholder="SMSC Live"
                data-testid="stream-title-input"
              />
            </div>

            <div>
              <Label className="text-white/80">Stream Description</Label>
              <Textarea
                value={formData.stream_description}
                onChange={(e) => setFormData({ ...formData, stream_description: e.target.value })}
                className="bg-casino-secondary border-white/10 text-white mt-1 min-h-[80px]"
                placeholder="Describe your stream..."
                data-testid="stream-description-input"
              />
            </div>

            <div>
              <Label className="text-white/80">Offline Thumbnail URL (optional)</Label>
              <Input
                value={formData.offline_thumbnail}
                onChange={(e) => setFormData({ ...formData, offline_thumbnail: e.target.value })}
                className="bg-casino-secondary border-white/10 text-white mt-1"
                placeholder="https://..."
                data-testid="offline-thumbnail-input"
              />
            </div>

            <Button
              type="submit"
              disabled={saving}
              className="w-full bg-gold text-black hover:bg-gold-dark"
              data-testid="save-stream-info-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Saving..." : "Save Stream Info"}
            </Button>
          </form>

          {/* VDO.Ninja Configuration */}
          <div className="glass-card p-6 space-y-4">
            <h2 className="font-heading text-xl text-white flex items-center gap-2">
              <Link className="w-5 h-5 text-gold" />
              VDO.Ninja URLs
            </h2>

            <div>
              <Label className="text-white/80">Room Name (optional)</Label>
              <Input
                value={formData.room_name}
                onChange={(e) => setFormData({ ...formData, room_name: e.target.value })}
                className="bg-casino-secondary border-white/10 text-white mt-1"
                placeholder="smsc-live"
                data-testid="room-name-input"
              />
            </div>

            <div>
              <Label className="text-white/80">VDO.Ninja Embed URL *</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={formData.vdo_embed_url}
                  onChange={(e) => setFormData({ ...formData, vdo_embed_url: e.target.value })}
                  className="bg-casino-secondary border-white/10 text-white flex-1"
                  placeholder="https://vdo.ninja/?view=..."
                  data-testid="vdo-embed-url-input"
                />
                {formData.vdo_embed_url && (
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => copyToClipboard(formData.vdo_embed_url, 'embed')}
                    className="border-white/20 text-white hover:border-gold"
                  >
                    {copiedField === 'embed' ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                  </Button>
                )}
              </div>
              <p className="text-white/40 text-xs mt-1">This is the URL shown to viewers on the public /live page</p>
            </div>

            <div>
              <Label className="text-white/80">VDO.Ninja View URL (for OBS)</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={formData.vdo_view_url}
                  onChange={(e) => setFormData({ ...formData, vdo_view_url: e.target.value })}
                  className="bg-casino-secondary border-white/10 text-white flex-1"
                  placeholder="https://vdo.ninja/?view=..."
                  data-testid="vdo-view-url-input"
                />
                {formData.vdo_view_url && (
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => copyToClipboard(formData.vdo_view_url, 'view')}
                    className="border-white/20 text-white hover:border-gold"
                  >
                    {copiedField === 'view' ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                  </Button>
                )}
              </div>
            </div>

            <div>
              <Label className="text-white/80">Guest Invite URL</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={formData.guest_invite_url}
                  onChange={(e) => setFormData({ ...formData, guest_invite_url: e.target.value })}
                  className="bg-casino-secondary border-white/10 text-white flex-1"
                  placeholder="https://vdo.ninja/?push=..."
                  data-testid="guest-invite-url-input"
                />
                {formData.guest_invite_url && (
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => copyToClipboard(formData.guest_invite_url, 'guest')}
                    className="border-white/20 text-white hover:border-gold"
                  >
                    {copiedField === 'guest' ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                  </Button>
                )}
              </div>
              <p className="text-white/40 text-xs mt-1">Share with guests to let them join your stream</p>
            </div>

            <Button
              type="button"
              onClick={handleSubmit}
              disabled={saving}
              className="w-full bg-gold text-black hover:bg-gold-dark"
              data-testid="save-vdo-config-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Saving..." : "Save VDO.Ninja Config"}
            </Button>
          </div>
        </div>

        {/* Right Column - Preview & Tools */}
        <div className="space-y-6">
          {/* Quick Copy Links */}
          <div className="glass-card p-6 space-y-4">
            <h2 className="font-heading text-xl text-white flex items-center gap-2">
              <Copy className="w-5 h-5 text-gold" />
              Quick Copy Links
            </h2>

            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-black/30 rounded-lg">
                <div className="flex items-center gap-2">
                  <Eye className="w-4 h-4 text-gold" />
                  <span className="text-white text-sm">Public Live Page</span>
                </div>
                <div className="flex items-center gap-2">
                  <code className="text-white/50 text-xs hidden sm:block">{publicLiveUrl}</code>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyToClipboard(publicLiveUrl, 'public')}
                    className="border-white/20 text-white hover:border-gold"
                  >
                    {copiedField === 'public' ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                  </Button>
                  <a href="/live" target="_blank" rel="noopener noreferrer">
                    <Button size="sm" variant="outline" className="border-white/20 text-white hover:border-gold">
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  </a>
                </div>
              </div>

              {formData.guest_invite_url && (
                <div className="flex items-center justify-between p-3 bg-black/30 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-gold" />
                    <span className="text-white text-sm">Guest Invite Link</span>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyToClipboard(formData.guest_invite_url, 'guestCopy')}
                    className="border-white/20 text-white hover:border-gold"
                  >
                    {copiedField === 'guestCopy' ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              )}

              {embedCode && (
                <div className="flex items-center justify-between p-3 bg-black/30 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Monitor className="w-4 h-4 text-gold" />
                    <span className="text-white text-sm">Embed Code</span>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyToClipboard(embedCode, 'embedCode')}
                    className="border-white/20 text-white hover:border-gold"
                  >
                    {copiedField === 'embedCode' ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Live Preview */}
          <div className="glass-card p-6">
            <h2 className="font-heading text-xl text-white flex items-center gap-2 mb-4">
              <Eye className="w-5 h-5 text-gold" />
              Preview
              {formData.is_live && (
                <span className="ml-2 px-2 py-0.5 bg-red-600 text-white text-xs rounded animate-pulse">LIVE</span>
              )}
            </h2>
            
            <div className="relative w-full bg-black rounded-lg overflow-hidden border border-white/10">
              <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
                {formData.is_live && formData.vdo_embed_url ? (
                  <iframe
                    src={formData.vdo_embed_url}
                    className="absolute inset-0 w-full h-full"
                    allow="autoplay; camera; microphone; fullscreen"
                    allowFullScreen
                    title="Stream Preview"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center bg-casino-dark">
                    <div className="text-center">
                      <Radio className="w-10 h-10 text-gold/30 mx-auto mb-2" />
                      <p className="text-white/40 text-sm">Stream Offline</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Complete Setup Guide - Full Width Below */}
      <div className="mt-8 glass-card p-6 space-y-6" data-testid="setup-guide">
        <h2 className="font-heading text-xl text-white flex items-center gap-2">
          <Info className="w-5 h-5 text-gold" />
          Complete Setup Guide: iPhones to Website
        </h2>
        <p className="text-white/50 text-sm">Follow these steps in order. The chain is: <strong className="text-white">iPhone Cameras &rarr; VDO.Ninja &rarr; OBS Studio (switching) &rarr; VDO.Ninja Output &rarr; This Website</strong></p>

            {/* STEP 1 */}
            <div className="border border-white/10 rounded-lg overflow-hidden">
              <div className="bg-gold/10 px-4 py-3 flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-gold text-black flex items-center justify-center shrink-0 text-sm font-bold">1</span>
                <h3 className="text-white font-bold">Create a VDO.Ninja Room</h3>
              </div>
              <div className="px-4 py-4 space-y-2 text-sm text-white/70">
                <p>This room is where your iPhones will send their video feeds.</p>
                <ol className="list-decimal list-inside space-y-2 ml-2">
                  <li>On your <strong className="text-white">computer</strong>, open a browser and go to <a href="https://vdo.ninja" target="_blank" rel="noopener noreferrer" className="text-gold hover:underline">https://vdo.ninja</a></li>
                  <li>Click <strong className="text-white">"Create a Room"</strong></li>
                  <li>Enter a room name: <code className="bg-black/50 px-2 py-0.5 rounded text-gold">smsc-live</code> (or any name you like)</li>
                  <li>Click <strong className="text-white">"Create the Room"</strong> &mdash; you'll land on the <strong className="text-white">Director</strong> page</li>
                  <li>You'll see an <strong className="text-white">"Invite a Guest"</strong> link that looks like:<br/>
                    <code className="block bg-black/50 px-2 py-1 rounded text-green-400 mt-1 text-xs break-all">https://vdo.ninja/?room=smsc-live&push=XXXXXX</code>
                  </li>
                  <li><strong className="text-gold">Copy this invite link</strong> &mdash; you'll need it for your iPhones in Step 2</li>
                  <li>Also paste it into the <strong className="text-white">"Guest Invite URL"</strong> field on the left side of this page, then click <strong className="text-white">"Save VDO.Ninja Config"</strong></li>
                </ol>
              </div>
            </div>

            {/* STEP 2 */}
            <div className="border border-white/10 rounded-lg overflow-hidden">
              <div className="bg-gold/10 px-4 py-3 flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-gold text-black flex items-center justify-center shrink-0 text-sm font-bold">2</span>
                <h3 className="text-white font-bold">Connect Your iPhones</h3>
              </div>
              <div className="px-4 py-4 space-y-2 text-sm text-white/70">
                <p>Each iPhone becomes a wireless camera. No app download needed &mdash; Safari is all you need.</p>
                <ol className="list-decimal list-inside space-y-2 ml-2">
                  <li>On <strong className="text-white">iPhone #1</strong>, open <strong className="text-white">Safari</strong></li>
                  <li>Paste the <strong className="text-white">Guest Invite link</strong> from Step 1 into the Safari address bar</li>
                  <li>Tap <strong className="text-white">"Allow"</strong> when Safari asks for camera and microphone permissions</li>
                  <li>Choose your preferred camera (front or back) and tap <strong className="text-white">"Start"</strong></li>
                  <li>Repeat for <strong className="text-white">iPhone #2</strong> (and any additional phones) using the <strong className="text-white">same invite link</strong></li>
                </ol>
                <div className="mt-3 p-3 bg-gold/10 border border-gold/20 rounded-lg">
                  <p className="text-gold text-xs"><strong>Important:</strong> All devices (iPhones and computer) must be on the <strong>same Wi-Fi network</strong> for the best performance and lowest latency.</p>
                </div>
                <p className="mt-2">Back on your <strong className="text-white">computer's Director page</strong> (from Step 1), you should now see both iPhone feeds appear. Each feed will have a small link icon &mdash; click it to get that camera's individual <strong className="text-white">solo view link</strong>:</p>
                <code className="block bg-black/50 px-2 py-1 rounded text-green-400 mt-1 text-xs break-all">https://vdo.ninja/?view=STREAM_ID_1&solo</code>
                <p className="mt-1"><strong className="text-gold">Copy each iPhone's solo view link separately</strong> &mdash; you'll paste these into OBS in Step 3.</p>
              </div>
            </div>

            {/* STEP 3 */}
            <div className="border border-white/10 rounded-lg overflow-hidden">
              <div className="bg-gold/10 px-4 py-3 flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-gold text-black flex items-center justify-center shrink-0 text-sm font-bold">3</span>
                <h3 className="text-white font-bold">Add iPhone Feeds to OBS Studio</h3>
              </div>
              <div className="px-4 py-4 space-y-2 text-sm text-white/70">
                <p>Now bring each iPhone camera into OBS so you can switch between them.</p>
                <ol className="list-decimal list-inside space-y-2 ml-2">
                  <li>Open <strong className="text-white">OBS Studio</strong> on your computer</li>
                  <li>In the <strong className="text-white">Scenes</strong> panel (bottom left), click <strong className="text-white">+</strong> to create a new scene. Name it <code className="bg-black/50 px-2 py-0.5 rounded text-gold">SMSC Live</code></li>
                  <li>In the <strong className="text-white">Sources</strong> panel (bottom center), click <strong className="text-white">+</strong></li>
                  <li>Select <strong className="text-white">"Browser"</strong> from the list</li>
                  <li>Name it <code className="bg-black/50 px-2 py-0.5 rounded text-gold">iPhone Camera 1</code> and click OK</li>
                  <li>In the properties window that opens, enter these settings:</li>
                </ol>

                <div className="mt-3 bg-black/50 rounded-lg p-4 space-y-2">
                  <p className="text-white font-medium text-xs uppercase tracking-wider mb-2">Browser Source Settings for iPhone Camera 1:</p>
                  <div className="grid grid-cols-[120px_1fr] gap-y-1.5 text-xs">
                    <span className="text-white/50">URL:</span>
                    <span className="text-green-400 break-all">Paste iPhone #1's solo view link from Step 2</span>
                    <span className="text-white/50">Width:</span>
                    <span className="text-white">1920</span>
                    <span className="text-white/50">Height:</span>
                    <span className="text-white">1080</span>
                    <span className="text-white/50">FPS:</span>
                    <span className="text-white">30</span>
                    <span className="text-white/50">Custom CSS:</span>
                    <code className="text-green-400">body {"{"} background-color: rgba(0,0,0,0); margin: 0px auto; overflow: hidden; {"}"}</code>
                  </div>
                  <div className="mt-2 space-y-1 text-xs">
                    <label className="flex items-center gap-2 text-white/70"><span className="text-green-400">[x]</span> Shutdown source when not visible</label>
                    <label className="flex items-center gap-2 text-white/70"><span className="text-green-400">[x]</span> Refresh browser when scene becomes active</label>
                  </div>
                </div>

                <ol className="list-decimal list-inside space-y-2 ml-2 mt-3" start="7">
                  <li>Click <strong className="text-white">OK</strong> &mdash; you should see the iPhone #1 feed in your OBS preview</li>
                  <li>Repeat steps 3&ndash;7 for <strong className="text-white">iPhone #2</strong> (name it <code className="bg-black/50 px-2 py-0.5 rounded text-gold">iPhone Camera 2</code> and paste its solo view link)</li>
                  <li>Resize and position each camera feed in OBS as desired</li>
                </ol>
                <div className="mt-3 p-3 bg-gold/10 border border-gold/20 rounded-lg">
                  <p className="text-gold text-xs"><strong>Pro Tip:</strong> Create separate OBS <strong>Scenes</strong> for each camera (e.g., "Table 1 Camera", "Table 2 Camera", "Both Cameras") so you can switch between views with one click during the stream.</p>
                </div>
              </div>
            </div>

            {/* STEP 4 */}
            <div className="border border-white/10 rounded-lg overflow-hidden">
              <div className="bg-gold/10 px-4 py-3 flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-gold text-black flex items-center justify-center shrink-0 text-sm font-bold">4</span>
                <h3 className="text-white font-bold">Output OBS Back Through VDO.Ninja</h3>
              </div>
              <div className="px-4 py-4 space-y-2 text-sm text-white/70">
                <p>This is the key step &mdash; getting your OBS output (with multi-camera switching) back into VDO.Ninja so the website can display it.</p>
                <ol className="list-decimal list-inside space-y-2 ml-2">
                  <li>In OBS, click <strong className="text-white">"Start Virtual Camera"</strong> (bottom right of OBS, next to "Start Recording")</li>
                  <li>Open a <strong className="text-white">new browser tab</strong> on your computer</li>
                  <li>Go to <a href="https://vdo.ninja" target="_blank" rel="noopener noreferrer" className="text-gold hover:underline">https://vdo.ninja</a></li>
                  <li>Click <strong className="text-white">"Share your Camera"</strong></li>
                  <li>In the <strong className="text-white">camera dropdown</strong>, select <strong className="text-white">"OBS Virtual Camera"</strong> (instead of your webcam)</li>
                  <li>Click <strong className="text-white">"Start"</strong></li>
                  <li>VDO.Ninja will generate a <strong className="text-white">view link</strong> that looks like:<br/>
                    <code className="block bg-black/50 px-2 py-1 rounded text-green-400 mt-1 text-xs break-all">https://vdo.ninja/?view=YYYYYY</code>
                  </li>
                  <li><strong className="text-gold">Copy this link</strong> &mdash; this is your final output URL for the website</li>
                </ol>
                <div className="mt-3 p-3 bg-red-900/30 border border-red-500/30 rounded-lg">
                  <p className="text-red-300 text-xs"><strong>Do NOT close this browser tab!</strong> This tab is what sends your OBS output to VDO.Ninja. If you close it, the website stream will stop. Minimize it instead.</p>
                </div>
              </div>
            </div>

            {/* STEP 5 */}
            <div className="border border-white/10 rounded-lg overflow-hidden">
              <div className="bg-gold/10 px-4 py-3 flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-gold text-black flex items-center justify-center shrink-0 text-sm font-bold">5</span>
                <h3 className="text-white font-bold">Configure the Website (This Page)</h3>
              </div>
              <div className="px-4 py-4 space-y-2 text-sm text-white/70">
                <p>Now paste the output link into this admin page so viewers can watch on your website.</p>
                <ol className="list-decimal list-inside space-y-2 ml-2">
                  <li>On the left side of this page, find the <strong className="text-white">"VDO.Ninja URLs"</strong> section</li>
                  <li>In the <strong className="text-white">"VDO.Ninja Embed URL"</strong> field, paste the view link from Step 4.7:<br/>
                    <code className="block bg-black/50 px-2 py-1 rounded text-green-400 mt-1 text-xs break-all">https://vdo.ninja/?view=YYYYYY</code>
                  </li>
                  <li>Fill in <strong className="text-white">"Room Name"</strong>: <code className="bg-black/50 px-2 py-0.5 rounded text-gold">smsc-live</code></li>
                  <li>Click <strong className="text-white">"Save VDO.Ninja Config"</strong></li>
                  <li>Click the <strong className="text-white">"Go Live"</strong> button at the top right of this page</li>
                </ol>

                <div className="mt-3 bg-black/50 rounded-lg p-4">
                  <p className="text-white font-medium text-xs uppercase tracking-wider mb-2">Quick Reference &mdash; What Goes Where:</p>
                  <div className="grid grid-cols-[1fr_24px_1fr] gap-y-2 text-xs items-center">
                    <span className="text-white/70">Guest Invite Link (Step 1)</span>
                    <span className="text-gold text-center">&rarr;</span>
                    <span className="text-white"><strong>Guest Invite URL</strong> field + each iPhone's Safari</span>

                    <span className="text-white/70">Each iPhone's solo view link (Step 2)</span>
                    <span className="text-gold text-center">&rarr;</span>
                    <span className="text-white"><strong>OBS Browser Source</strong> URL (one per camera)</span>

                    <span className="text-white/70">OBS Virtual Camera output link (Step 4)</span>
                    <span className="text-gold text-center">&rarr;</span>
                    <span className="text-white"><strong>VDO.Ninja Embed URL</strong> field on this page</span>
                  </div>
                </div>
              </div>
            </div>

            {/* STEP 6 */}
            <div className="border border-white/10 rounded-lg overflow-hidden">
              <div className="bg-gold/10 px-4 py-3 flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-gold text-black flex items-center justify-center shrink-0 text-sm font-bold">6</span>
                <h3 className="text-white font-bold">Verify Everything Works</h3>
              </div>
              <div className="px-4 py-4 space-y-2 text-sm text-white/70">
                <ol className="list-decimal list-inside space-y-2 ml-2">
                  <li>Check the <strong className="text-white">Preview</strong> panel above &mdash; you should see your OBS output</li>
                  <li>Open your website in a new tab and go to the <strong className="text-white">/live</strong> page</li>
                  <li>You should see whatever OBS is currently showing (your iPhone camera feeds)</li>
                  <li>Switch scenes in OBS &mdash; the website stream should update within 1&ndash;2 seconds</li>
                </ol>
                <div className="mt-3 p-3 bg-gold/10 border border-gold/20 rounded-lg">
                  <p className="text-gold text-xs"><strong>When you're done streaming:</strong> Click <strong>"Go Offline"</strong> at the top of this page. Then stop the Virtual Camera in OBS, and close the VDO.Ninja tabs. The website will automatically show the "Stream Offline" message.</p>
                </div>
              </div>
            </div>

            {/* Troubleshooting */}
            <div className="border border-red-500/20 rounded-lg overflow-hidden">
              <div className="bg-red-900/20 px-4 py-3">
                <h3 className="text-white font-bold text-sm">Troubleshooting</h3>
              </div>
              <div className="px-4 py-4 space-y-3 text-sm text-white/70">
                <div className="flex gap-2">
                  <span className="text-red-400 shrink-0">&bull;</span>
                  <span><strong className="text-white">No video in OBS:</strong> Right-click the Browser Source &rarr; Properties &rarr; click "Refresh cache of current page". Make sure the VDO.Ninja link is correct.</span>
                </div>
                <div className="flex gap-2">
                  <span className="text-red-400 shrink-0">&bull;</span>
                  <span><strong className="text-white">iPhone camera not connecting:</strong> Make sure you're using <strong>Safari</strong> (not Chrome). Check that camera permissions are allowed. All devices must be on the <strong>same Wi-Fi network</strong>.</span>
                </div>
                <div className="flex gap-2">
                  <span className="text-red-400 shrink-0">&bull;</span>
                  <span><strong className="text-white">Laggy or choppy feed:</strong> Lower the resolution on the iPhone VDO.Ninja page to 720p instead of 1080p. Make sure no other heavy apps are running on the computer.</span>
                </div>
                <div className="flex gap-2">
                  <span className="text-red-400 shrink-0">&bull;</span>
                  <span><strong className="text-white">"OBS Virtual Camera" not in dropdown:</strong> Make sure you clicked "Start Virtual Camera" in OBS first (Step 4.1). If it still doesn't appear, restart your browser.</span>
                </div>
                <div className="flex gap-2">
                  <span className="text-red-400 shrink-0">&bull;</span>
                  <span><strong className="text-white">Website shows "Stream Offline":</strong> Make sure you clicked "Go Live" on this page AND that the VDO.Ninja Embed URL field is filled in correctly.</span>
                </div>
              </div>
            </div>
          </div>
    </div>
  );
}
